/**
 * `api/settings.ts` — typed wrapper around the `headless-bridge` plugin's
 * unified `/wp-json/{namespace}/settings` endpoint.
 *
 * The plugin composes everything a layout / SEO component needs from a
 * fresh visitor's first request into a single JSON document (Req 2.2):
 * site name, tagline, logo, favicon, homepage assignment, footer,
 * contact info, social links, and the deployment-level `siteOrigin`
 * used by the Frontend's canonical URL builder. Fetching one document
 * — instead of five WP core option calls — keeps the initial-render
 * critical path short (one round-trip primes Navbar, Footer, SEOHead,
 * and the sitemap generator at build time).
 *
 * Design contract:
 * - Routes HTTP traffic exclusively through the shared `apiClient` from
 *   `./client`; never constructs an Axios instance of its own
 *   (Req 4.5, Req 16.6).
 * - Returns the strongly-typed `SiteSettings` shape declared in
 *   `@/types/settings`; snake_case / partially-populated CMS
 *   responses never leak past this module (Req 4.1, 4.2).
 * - Defensive normalization: the plugin should always return the
 *   documented shape, but a temporarily disabled plugin, a partially-
 *   filled settings screen, or a mid-migration CMS may return null-ish
 *   or missing fields. The normalizer degrades every optional field to
 *   its documented default (empty string, empty array, `null`) so the
 *   layout components render without additional guards.
 *
 * Requirements: 2.2, 4.1, 4.2.
 */

import { apiClient } from './client';
import { normalizeMedia } from './media';
import type { ContactInfo, MaintenanceSettings, Media, SiteSettings, SocialLink } from '@/types';

/**
 * REST namespace used by the `headless-bridge` plugin. Read from
 * `VITE_HEADLESS_NAMESPACE` so a deployment that reconfigures the CMS
 * namespace (see `HEADLESS_BRIDGE_NAMESPACE` on the WordPress side)
 * does not require a Frontend code change. Defaults to `headless/v1`
 * to match the plugin's own default (see `cms/wp-content/plugins/
 * headless-bridge/headless-bridge.php`).
 */
const NAMESPACE: string =
  (import.meta.env.VITE_HEADLESS_NAMESPACE as string | undefined) ??
  'headless/v1';

/**
 * Fetch the unified `SiteSettings` document (Req 2.2, 4.1, 4.2).
 *
 * Rejects with an `ApiError` on any non-2xx response, timeout, or
 * network failure — see the shared response interceptor in
 * `./client.ts` (Req 4.4).
 */
export async function getSettings(): Promise<SiteSettings> {
  const { data } = await apiClient.get<unknown>(
    `/wp-json/${NAMESPACE}/settings`,
  );
  return normalizeSettings(data);
}

/**
 * Convert a raw `/settings` response into the strongly-typed
 * `SiteSettings` shape consumed by the rest of the Frontend (Req 4.2).
 *
 * The plugin already emits camelCase keys (see the PHP handler in
 * `cms/wp-content/plugins/headless-bridge/includes/class-rest-settings.php`),
 * so this normalizer's job is validation and defaulting rather than
 * case conversion:
 *
 * - Non-object input (null, arrays, strings, transport-level garbage)
 *   collapses to a fully-populated empty settings document. Callers
 *   never see `undefined` fields.
 * - `siteName`, `tagline`, `siteOrigin` — coerced to string; empty
 *   string when absent.
 * - `logo`, `favicon` — routed through `normalizeMediaOrNull` so the
 *   Media shape matches `api/media.ts` output exactly, and any
 *   plugin-emitted `null` (or missing field) surfaces as `null`.
 * - `homepageSlug` — string when present and non-empty, otherwise
 *   `null`. The plugin uses `null` to signal "show latest posts on `/`"
 *   (see `resolve_homepage_slug` in the PHP handler).
 * - `footer` — `{ html, copyright }` with both keys defaulted to `''`.
 * - `contact` — every field defaults to `null` (matches the plugin's
 *   "missing entries returned as null" contract).
 * - `social` — array; entries missing `platform` or `url` are dropped
 *   (mirrors the plugin-side filter for defense in depth).
 */
export function normalizeSettings(raw: unknown): SiteSettings {
  if (!isRecord(raw)) {
    return emptySettings();
  }

  return {
    siteName: toStr(raw.siteName),
    tagline: toStr(raw.tagline),
    logo: normalizeMediaOrNull(raw.logo),
    favicon: normalizeMediaOrNull(raw.favicon),
    homepageSlug: toStringOrNull(raw.homepageSlug),
    footer: normalizeFooter(raw.footer),
    contact: normalizeContact(raw.contact),
    social: normalizeSocial(raw.social),
    siteOrigin: toStr(raw.siteOrigin),
    maintenance: normalizeMaintenance(raw.maintenance),
  };
}

/**
 * Normalize a raw `logo` / `favicon` slot from the settings payload.
 *
 * Returns `null` when the slot is absent, explicitly null, or not an
 * object — the plugin uses `null` to signal "no logo configured" and
 * the Frontend renders the appropriate fallback (site name text,
 * default favicon) in that case. Otherwise reuses `normalizeMedia`
 * from `./media` so the resulting shape is byte-identical to what the
 * `/wp/v2/media/{id}` helpers produce (Req 2.5, 4.2).
 *
 * Exported so `useSettings` unit tests, the SEO fallback helper, and
 * any future settings-adjacent module can reuse the same coercion
 * without duplicating the null-check semantics.
 */
export function normalizeMediaOrNull(raw: unknown): Media | null {
  if (!isRecord(raw)) {
    return null;
  }
  const media = normalizeMedia(raw);
  // A fully empty Media (id === 0 AND no source URL) is
  // indistinguishable from "no media configured" — treat it as null
  // so downstream components can rely on a simple truthiness check.
  if (media.id === 0 && media.sourceUrl === '') {
    return null;
  }
  return media;
}

// ---------------------------------------------------------------------------
// Internal helpers (not exported).
// ---------------------------------------------------------------------------

/**
 * Baseline `SiteSettings` returned when the CMS is unreachable at the
 * shape level (non-object payload). Every field is at its documented
 * "absent" default so layout components render a coherent empty state
 * without additional guards.
 */
function emptySettings(): SiteSettings {
  return {
    siteName: '',
    tagline: '',
    logo: null,
    favicon: null,
    homepageSlug: null,
    footer: { html: '', copyright: '' },
    contact: { email: null, phone: null, address: null },
    social: [],
    siteOrigin: '',
    maintenance: { enabled: false, message: '', bypassToken: '' },
  };
}

function normalizeFooter(raw: unknown): SiteSettings['footer'] {
  if (!isRecord(raw)) {
    return { html: '', copyright: '' };
  }
  return {
    html: toStr(raw.html),
    copyright: toStr(raw.copyright),
  };
}

function normalizeContact(raw: unknown): ContactInfo {
  if (!isRecord(raw)) {
    return { email: null, phone: null, address: null };
  }
  return {
    email: toStringOrNull(raw.email),
    phone: toStringOrNull(raw.phone),
    address: toStringOrNull(raw.address),
  };
}

function normalizeSocial(raw: unknown): SocialLink[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const links: SocialLink[] = [];
  for (const entry of raw) {
    if (!isRecord(entry)) {
      continue;
    }
    const platform = toStr(entry.platform);
    const url = toStr(entry.url);
    // Mirror the plugin-side filter: rows without both a platform and
    // a URL are useless to render, so drop them here as well.
    if (platform === '' || url === '') {
      continue;
    }
    links.push({ platform, url });
  }
  return links;
}

function normalizeMaintenance(raw: unknown): MaintenanceSettings {
  if (!isRecord(raw)) {
    return { enabled: false, message: '', bypassToken: '' };
  }
  return {
    enabled: raw.enabled === true,
    message: toStr(raw.message),
    bypassToken: toStr(raw.bypassToken),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function toStr(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * Coerce an arbitrary value to `string | null`. Non-empty strings are
 * preserved verbatim; empty strings, `null`, `undefined`, and every
 * non-string value collapse to `null`. This matches the plugin's
 * "missing entries returned as null" contract for `homepageSlug` and
 * every `ContactInfo` field.
 */
function toStringOrNull(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  return value === '' ? null : value;
}
